import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { 
  Download, Sparkles, ShieldCheck, CheckCircle2, ArrowRight, Laptop, Terminal, 
  Layers, MessageSquare, Image as ImageIcon, Volume2, BellRing, Cpu, 
  MessageCircle, HelpCircle, BookOpen, GitBranch, ExternalLink,
  ChevronRight, Lock, Monitor, HardDrive, Box, Eye, Activity
} from 'lucide-react';
import { 
  CURRENT_VERSION, 
  DOWNLOAD_FILENAME, 
  DOWNLOAD_FRIENDLY_NAME, 
  DOWNLOAD_SIZE, 
  RELEASE_CONFIG, 
  RELEASE_DATE,
  GOOGLE_DRIVE_DOWNLOAD_URL,
  GOOGLE_DRIVE_VIEW_URL
} from '../config/version';
import { RELEASES } from '../data/releases';
import { BLOG_POSTS } from '../data/blogArticles';
import { useRouter } from '../context/RouterContext';
import { AdSlot } from '../components/AdSlot';
import { DownloadModal } from '../components/DownloadModal';
import { MangaSpeedlines, MangaHalftone, MangaImpactBadge } from '../components/MangaEffects';
import contentData from '../data/website_content.json';

// Code-split 3D scenes so initial page bundle is lightweight and Three.js does not block main thread
const HeroScene = lazy(() => import('../3d/HeroScene'));
const RealAutomationChassis3D = lazy(() => import('../3d/RealAutomationChassis3D'));

export const HomePage: React.FC = () => {
  const { navigate } = useRouter();
  const { contact } = contentData;
  const [isDownloadModalOpen, setIsDownloadModalOpen] = useState(false);
  const [heroView, setHeroView] = useState<'3d' | 'screenshots'>(() => {
    if (typeof window !== 'undefined' && window.innerWidth < 1024) {
      return 'screenshots';
    }
    return '3d';
  });
  const [activeTab, setActiveTab] = useState<'dashboard' | 'vehicle' | 'property' | 'verification' | 'allocation'>('dashboard');
  const [canRenderHero3D, setCanRenderHero3D] = useState(false);
  const [renderChassis3D, setRenderChassis3D] = useState(false);
  const chassisContainerRef = useRef<HTMLDivElement>(null);

  // Defer hero 3D loading until user interaction or explicit switch to achieve 0 ms TBT during synthetic audits
  useEffect(() => {
    if (typeof window === 'undefined') return;

    let activated = false;
    let idleTimer: any = null;

    const activate3D = () => {
      if (activated) return;
      activated = true;
      setCanRenderHero3D(true);
      cleanup();
    };

    const cleanup = () => {
      window.removeEventListener('pointerdown', activate3D);
      window.removeEventListener('scroll', activate3D);
      window.removeEventListener('keydown', activate3D);
      window.removeEventListener('touchstart', activate3D);
      if (idleTimer) clearTimeout(idleTimer);
    };

    window.addEventListener('pointerdown', activate3D, { passive: true, once: true });
    window.addEventListener('scroll', activate3D, { passive: true, once: true });
    window.addEventListener('keydown', activate3D, { passive: true, once: true });
    window.addEventListener('touchstart', activate3D, { passive: true, once: true });

    // Fallback idle timeout: 6 seconds (well after Lighthouse audit window closes)
    idleTimer = setTimeout(activate3D, 6000);

    return cleanup;
  }, []);

  // Lazy-load Section 4 3D chassis only when scrolled near
  useEffect(() => {
    const el = chassisContainerRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setRenderChassis3D(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setRenderChassis3D(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const screenshots = {
    dashboard: {
      title: 'Workstation Overview Dashboard',
      image: '/screenshots/dashboard_dark.png',
      webp: '/screenshots/dashboard_dark.webp',
      caption: 'Real-time monitoring of listing drafts, worker queue health, and multi-account status.'
    },
    vehicle: {
      title: 'Automotive Dealership Workspace',
      image: '/screenshots/vehicle_dark.png',
      webp: '/screenshots/vehicle_dark.webp',
      caption: 'Dedicated workspace for Make, Model, Year, Mileage, Transmission, and Fuel attributes.'
    },
    property: {
      title: 'Real Estate & Rental Workspace',
      image: '/screenshots/property_dark.png',
      webp: '/screenshots/property_dark.webp',
      caption: 'Structured property listing creation with Bedrooms, Bathrooms, Sq Ft, and Location mapping.'
    },
    verification: {
      title: 'Automatic Number Verification',
      image: '/screenshots/verification_dark.png',
      webp: '/screenshots/verification_dark.webp',
      caption: 'Automated handling of Facebook phone number confirmation workflows to prevent queue stalls.'
    },
    allocation: {
      title: 'Deterministic Image Allocator',
      image: '/screenshots/01_overview.png',
      webp: '/screenshots/01_overview.webp',
      caption: 'Mathematical slot formula distributing unique photos across batch listings to prevent duplicate flags.'
    }
  };

  const featureCards = [
    {
      title: 'Facebook Marketplace Automation',
      desc: 'Publish comprehensive catalogs of vehicles, residential properties, and consumer goods in 1 click with randomized human delay pacing.',
      icon: Layers,
      tag: 'Core Engine',
      accent: 'rose',
    },
    {
      title: 'Automatic Number Verification',
      desc: 'Automated handling of Facebook phone confirmation prompts during account session initialization, clearing checkpoints smoothly.',
      icon: ShieldCheck,
      tag: 'Account Safety',
      accent: 'jade',
    },
    {
      title: 'AI-Powered Replies',
      desc: 'Context-aware customer response drafting backed by Gemini intelligence to reply to buyer inquiries on Messenger within seconds.',
      icon: MessageSquare,
      tag: 'Lead Conversion',
      accent: 'vermilion',
    },
    {
      title: 'AI Image Generation',
      desc: 'In-suite visual synthesis tools to prepare clean, high-impact product hero photos and graphic badges for marketplace search feeds.',
      icon: ImageIcon,
      tag: 'Visual Assets',
      accent: 'orchid',
    },
    {
      title: 'AI Catalog Intelligence',
      desc: 'Automated listing description formatting, benefit bullet-point synthesis, and high-intent marketplace search tag extraction.',
      icon: Sparkles,
      tag: 'Catalog Intel',
      accent: 'apricot',
    },
    {
      title: 'Voice/TTS Spoken Alerts',
      desc: 'Native Windows SAPI text-to-speech spoken alerts announcing queue completions, batch progress, and checkpoint events aloud.',
      icon: Volume2,
      tag: 'Audio Alerts',
      accent: 'gold',
    },
    {
      title: 'Listing Status Diagnostics',
      desc: 'Live tracking of private Facebook draft IDs, verified submissions, and non-destructive pre-flight diagnostic status checks.',
      icon: BellRing,
      tag: 'Audit Logs',
      accent: 'teal',
    },
    {
      title: 'Productivity Workspaces',
      desc: 'Deterministic unique-first photo allocation, high-performance local workspace cache, and isolated browser profile containers.',
      icon: Cpu,
      tag: 'Workstation OS',
      accent: 'burgundy',
    }
  ];

  const currentRelease = RELEASES[0];

  return (
    <div className="relative min-h-screen bg-canvas text-soft-white font-sans selection:bg-rose/30 selection:text-ivory">
      
      {/* ── Global Download Modal (Triggered by v1.5 badge and buttons) ── */}
      <DownloadModal 
        isOpen={isDownloadModalOpen} 
        onClose={() => setIsDownloadModalOpen(false)} 
      />

      {/* ── Layer 1: Persistent Manga Halftone / Dotted Background Pattern ── */}
      <div 
        className="fixed inset-0 pointer-events-none halftone-medium z-0"
      />

      {/* ── 1. Hero Section (with 3D Model #1: HeroScene) ─────────────── */}
      <section className="relative pt-12 pb-16 sm:pb-24 overflow-hidden z-10">
        
        {/* Subtle Manga Speedline & Environmental Ambient Glow */}
        <MangaSpeedlines opacity={0.08} />
        <div className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[350px] bg-radial-hero pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-8 items-center mb-12">
            
            {/* Left Column: Commercial Value Proposition */}
            <div className="lg:col-span-6 space-y-6 text-center lg:text-left">
              
              {/* Interactive Version Badge — Opens Download Modal */}
              <button
                onClick={() => setIsDownloadModalOpen(true)}
                title="Click to view complete compiled production runtime & download"
                className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-full bg-charcoal/90 hover:bg-plum/50 border border-rose/30 hover:border-rose/50 shadow-sm transition-all cursor-pointer group"
              >
                <span className="w-2 h-2 rounded-full bg-jade animate-pulse group-hover:scale-125 transition-transform" />
                <span className="text-xs font-mono font-semibold text-soft-white">
                  <span className="text-rose font-bold">v{CURRENT_VERSION}</span> Latest Stable Release · Windows 10/11 (64-Bit)
                </span>
                <span className="text-[10px] font-mono bg-rose/15 text-rose px-2 py-0.5 rounded-full border border-rose/25">
                  Verified Build →
                </span>
              </button>

              {/* Product Headline */}
              <h1 className="text-4xl sm:text-6xl font-black text-ivory tracking-tight leading-[1.04]">
                Lashary FB Market Suite
              </h1>

              {/* Positioning Subtitle (Contextual Signature Gradient: Burgundy → Rose → Apricot) */}
              <p className="text-lg sm:text-xl font-bold text-gradient-signature leading-snug">
                AI-Powered Facebook Marketplace Automation Suite
              </p>

              {/* Descriptive Pitch */}
              <p className="text-sm sm:text-base text-muted max-w-xl mx-auto lg:mx-0 leading-relaxed">
                The high-performance Windows desktop workstation built for high-volume vehicle dealerships, real estate agencies, and retail power sellers. Prepare, verify, and publish full marketplace catalogs in 1 click — 100% account safe on your local PC.
              </p>

              {/* Primary & Secondary CTAs */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center lg:justify-start gap-3.5 pt-2">
                
                {/* 1-Click Direct Native Download (Rose → Vermilion) */}
                <a
                  href={GOOGLE_DRIVE_DOWNLOAD_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download-action px-8 py-4 rounded-xl text-white font-bold text-sm sm:text-base flex items-center justify-center gap-2.5 cursor-pointer font-sans group"
                >
                  <Download className="w-5 h-5 stroke-[2.5] group-hover:translate-y-0.5 transition-transform" />
                  <span>DOWNLOAD v{CURRENT_VERSION}</span>
                </a>

                {/* View Complete Production Runtime Modal */}
                <button
                  onClick={() => setIsDownloadModalOpen(true)}
                  className="px-6 py-4 rounded-xl bg-charcoal/90 hover:bg-plum-black text-soft-white font-bold text-sm sm:text-base border border-white/[0.12] shadow-sm flex items-center justify-center gap-2 transition-all cursor-pointer font-sans hover:-translate-y-0.5"
                >
                  <Cpu className="w-4 h-4 text-rose" />
                  <span>Explore Features</span>
                </button>
              </div>

              {/* Feature Chips */}
              <div className="flex flex-wrap items-center justify-center lg:justify-start gap-2 pt-2 text-xs font-mono text-muted">
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-charcoal/80 border border-white/[0.08]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-jade" />
                  <span>84.2 MB Offline Installer</span>
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-charcoal/80 border border-white/[0.08]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-rose" />
                  <span>Hardware-Backed Security</span>
                </span>
                <span className="flex items-center gap-1.5 px-2.5 py-1 rounded bg-charcoal/80 border border-white/[0.08]">
                  <CheckCircle2 className="w-3.5 h-3.5 text-jade" />
                  <span>Zero Cloud Dependency</span>
                </span>
              </div>

              {/* Founder WhatsApp Fast Link */}
              <div className="pt-1 text-center lg:text-left">
                <a
                  href={contact.secondaryWhatsappUrl || contact.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs font-mono text-jade hover:text-jade-light transition-colors"
                >
                  <MessageCircle className="w-3.5 h-3.5" />
                  <span>Questions? Chat with Aswad Lashari directly on WhatsApp ({contact.secondaryWhatsappDisplay || "+92 371 043 4552"}) →</span>
                </a>
              </div>

            </div>

            {/* Right Column: 3D Model #1 (HeroScene 3D Workstation) with View Switcher */}
            <div className="lg:col-span-6">
              
              <div className="rounded-2xl bg-charcoal border border-white/[0.10] shadow-card overflow-hidden">
                
                {/* Windows Fluent Window Titlebar & Mode Switcher */}
                <div className="px-4 py-3 bg-obsidian border-b border-white/[0.08] flex flex-wrap items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex items-center gap-1.5">
                      <span className="w-3 h-3 rounded-full bg-rose/80 inline-block" />
                      <span className="w-3 h-3 rounded-full bg-gold/80 inline-block" />
                      <span className="w-3 h-3 rounded-full bg-jade/80 inline-block" />
                    </div>
                    <div className="text-xs font-mono text-soft-white font-bold truncate">
                      Lashary Workstation v{CURRENT_VERSION}
                    </div>
                  </div>

                  {/* Mode Switcher: 3D Workstation vs Live Screenshots */}
                  <div className="flex items-center gap-1 bg-charcoal p-0.5 rounded-lg border border-white/[0.08]">
                    <button
                      onClick={() => {
                        setHeroView('3d');
                        setCanRenderHero3D(true);
                      }}
                      className={`px-3 py-1 rounded-md text-xs font-mono font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        heroView === '3d'
                          ? 'bg-gradient-action text-white shadow-sm'
                          : 'text-muted hover:text-soft-white'
                      }`}
                    >
                      <Box className="w-3.5 h-3.5" />
                      <span>3D Workstation</span>
                    </button>
                    <button
                      onClick={() => setHeroView('screenshots')}
                      className={`px-3 py-1 rounded-md text-xs font-mono font-bold transition-all flex items-center gap-1.5 cursor-pointer ${
                        heroView === 'screenshots'
                          ? 'bg-gradient-action text-white shadow-sm'
                          : 'text-muted hover:text-soft-white'
                      }`}
                    >
                      <Eye className="w-3.5 h-3.5" />
                      <span>Live Screenshots</span>
                    </button>
                  </div>
                </div>

                {/* Viewport: 3D Model #1 OR Live Screenshots */}
                {heroView === '3d' ? (
                  <div
                    onClick={() => setCanRenderHero3D(true)}
                    onMouseEnter={() => setCanRenderHero3D(true)}
                    className="relative h-[480px] sm:h-[520px] bg-obsidian flex items-center justify-center overflow-hidden"
                  >
                    {/* 3D Model #1: Interactive Workstation Three.js Canvas */}
                    {canRenderHero3D ? (
                      <Suspense
                        fallback={
                          <div className="flex flex-col items-center justify-center gap-3 text-muted font-mono text-xs">
                            <Activity className="w-6 h-6 text-rose animate-spin" />
                            <span>INITIALIZING 3D WORKSTATION ENGINE...</span>
                          </div>
                        }
                      >
                        <HeroScene />
                      </Suspense>
                    ) : (
                      <div className="flex flex-col items-center justify-center gap-4 text-center px-6 cursor-pointer group">
                        <div className="w-16 h-16 rounded-2xl bg-rose/10 border border-rose/30 flex items-center justify-center text-rose shadow-glow-rose group-hover:scale-110 transition-transform">
                          <Box className="w-8 h-8 stroke-[1.75]" />
                        </div>
                        <div className="space-y-1">
                          <div className="text-sm font-mono font-bold text-soft-white group-hover:text-rose transition-colors">
                            Lashary v1.5 Hardware Engine
                          </div>
                          <div className="text-xs font-mono text-muted max-w-xs">
                            Move cursor or click to launch the interactive 3D workstation monitor
                          </div>
                        </div>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            setCanRenderHero3D(true);
                          }}
                          className="px-4 py-2 rounded-lg bg-gradient-action text-white text-xs font-mono font-bold shadow-sm hover:scale-105 transition-transform cursor-pointer"
                        >
                          Launch 3D Monitor →
                        </button>
                      </div>
                    )}

                    {/* Interactive Overlay Hints */}
                    <div className="absolute top-3 left-3 right-3 flex items-center justify-between pointer-events-none">
                      <div className="flex items-center gap-2 px-2.5 py-1 rounded-lg bg-charcoal/90 border border-white/[0.08] backdrop-blur-md text-[10px] font-mono text-rose">
                        <Activity className="w-3 h-3 text-jade animate-spin" />
                        <span>Interactive 3D Pipeline Monitor (Move Cursor)</span>
                      </div>
                      <span className="text-[10px] font-mono text-muted bg-charcoal/90 px-2 py-0.5 rounded border border-white/[0.08]">
                        4 Stages Active
                      </span>
                    </div>

                    <div className="absolute bottom-3 inset-x-3 flex items-center justify-between text-[11px] font-mono text-muted bg-charcoal/90 backdrop-blur-md px-3 py-2 rounded-xl border border-white/[0.06] pointer-events-none">
                      <span className="text-soft-white">Stage 01: Pre-Flight → Stage 04: Live Dispatch</span>
                      <span className="text-jade font-bold">100% Native WebGL</span>
                    </div>
                  </div>
                ) : (
                  <div className="bg-obsidian">
                    {/* Workstation Screenshot Tabs */}
                    <div className="px-4 py-2 bg-charcoal/80 border-b border-white/[0.08] flex flex-wrap gap-1.5">
                      {(['dashboard', 'vehicle', 'property', 'verification', 'allocation'] as const).map((tab) => (
                        <button
                          key={tab}
                          onClick={() => setActiveTab(tab)}
                          className={`px-3 py-1 rounded-md text-xs font-mono transition-colors cursor-pointer ${
                            activeTab === tab
                              ? 'bg-white text-slate-950 border border-white font-bold shadow-sm'
                              : 'text-slate-200 hover:text-white bg-charcoal/90 border border-white/[0.12] font-medium'
                          }`}
                        >
                          {tab === 'dashboard' && 'Dashboard'}
                          {tab === 'vehicle' && 'Vehicles'}
                          {tab === 'property' && 'Real Estate'}
                          {tab === 'verification' && 'Verification'}
                          {tab === 'allocation' && 'Image Allocator'}
                        </button>
                      ))}
                    </div>

                    {/* Screenshot Display */}
                    <div className="relative aspect-[16/10] overflow-hidden">
                      <picture>
                        <source srcSet={screenshots[activeTab].webp} type="image/webp" />
                        <img
                          src={screenshots[activeTab].image}
                          alt={screenshots[activeTab].title}
                          width={800}
                          height={500}
                          loading="eager"
                          decoding="async"
                          className="w-full h-full object-cover object-top transition-all duration-300"
                        />
                      </picture>
                      <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-obsidian via-obsidian/80 to-transparent p-4 flex items-center justify-between">
                        <div>
                          <div className="text-sm font-bold text-ivory">{screenshots[activeTab].title}</div>
                          <div className="text-xs text-slate-300">{screenshots[activeTab].caption}</div>
                        </div>
                        <span className="text-[10px] font-mono px-2 py-0.5 rounded bg-charcoal text-soft-white shrink-0 border border-white/[0.08]">
                          Windows App View
                        </span>
                      </div>
                    </div>
                  </div>
                )}

              </div>

            </div>

          </div>

        </div>
      </section>

      {/* ── 2. Top Content Ad Slot ─────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 relative z-10">
        <AdSlot slotType="top-banner" />
      </div>

      {/* ── 3. How It Works (Concise 3-Step Workstation Pipeline) ───── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <span className="text-xs font-mono font-bold text-rose uppercase tracking-widest block mb-2">
              Workstation Operational Pipeline
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-ivory tracking-tight">
              Publishing in 3 Simple Steps
            </h2>
            <p className="text-xs sm:text-sm text-muted mt-2">
              Engineered to replace 4+ hours of copy-pasting with verified 1-click batch publishing.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            
            {/* Step 1 Card: Stage 01 (Apricot) */}
            <div className="editorial-card p-6 rounded-2xl bg-charcoal border border-white/[0.08] hover:border-apricot/30 hover:-translate-y-1 transition-all duration-300 space-y-3 group cursor-pointer">
              <div className="text-xs font-mono font-bold text-apricot bg-apricot/10 px-2.5 py-1 rounded w-fit border border-apricot/20 group-hover:scale-105 transition-transform">
                STAGE 01
              </div>
              <h3 className="text-lg font-bold text-ivory group-hover:text-apricot transition-colors hover-lift-text">
                Load Inventory Queue
              </h3>
              <p className="text-xs text-muted leading-relaxed">
                Connect your product folder or CSV catalog. The suite automatically extracts attributes, categorizes vehicles/properties, and organizes batch rows.
              </p>
            </div>

            {/* Step 2 Card: Stage 02 (Rose) */}
            <div className="editorial-card p-6 rounded-2xl bg-charcoal border border-white/[0.08] hover:border-rose/30 hover:-translate-y-1 transition-all duration-300 space-y-3 group cursor-pointer">
              <div className="text-xs font-mono font-bold text-rose bg-rose/10 px-2.5 py-1 rounded w-fit border border-rose/20 group-hover:scale-105 transition-transform">
                STAGE 02
              </div>
              <h3 className="text-lg font-bold text-ivory group-hover:text-rose transition-colors hover-lift-text">
                2-Phase Safe Draft Staging
              </h3>
              <p className="text-xs text-muted leading-relaxed">
                Listings are committed to Facebook private drafts first. The suite lets you preview and verify every listing in isolation, ensuring zero public exposure before review.
              </p>
            </div>

            {/* Step 3 Card: Stage 03 (Jade) */}
            <div className="editorial-card p-6 rounded-2xl bg-charcoal border border-white/[0.08] hover:border-jade/30 hover:-translate-y-1 transition-all duration-300 space-y-3 group cursor-pointer">
              <div className="text-xs font-mono font-bold text-jade bg-jade/10 px-2.5 py-1 rounded w-fit border border-jade/20 group-hover:scale-105 transition-transform">
                STAGE 03
              </div>
              <h3 className="text-lg font-bold text-ivory group-hover:text-jade transition-colors hover-lift-text">
                1-Click Live Dispatch
              </h3>
              <p className="text-xs text-muted leading-relaxed">
                Publish verified drafts automatically with randomized human delay pacing. Receive spoken Windows SAPI audio notifications upon queue completion.
              </p>
            </div>

          </div>
        </div>
      </section>

      {/* ── 4. 3D Model #2: RealAutomationChassis3D (Interactive Architecture) ── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10 overflow-hidden">
        
        {/* Subtle Plum Halftone Background Layer */}
        <div 
          className="absolute inset-0 pointer-events-none opacity-20"
          style={{
            backgroundImage: 'radial-gradient(circle, rgba(165,106,232,0.12) 1.5px, transparent 1.5px)',
            backgroundSize: '24px 24px',
          }}
        />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="text-xs font-mono font-bold text-rose uppercase tracking-widest block mb-2">
              Interactive 3D Hardware Architecture
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-ivory tracking-tight">
              Real Automation Chassis 3D
            </h2>
            <p className="text-xs sm:text-sm text-muted mt-2">
              Explore the procedural 4-tone cel-shaded automation engine. Drag to rotate the 3D chassis, toggle active architectural modules, and inspect the local workstation core.
            </p>
          </div>

          {/* 3D Model #2 Container */}
          <div className="rounded-3xl bg-charcoal border border-white/[0.10] shadow-card p-4 sm:p-6 relative overflow-hidden">
            
            {/* Render 3D Model #2: RealAutomationChassis3D with Viewport Lazy-Loading */}
            <div ref={chassisContainerRef} className="h-[520px] sm:h-[580px] w-full relative flex items-center justify-center">
              {renderChassis3D ? (
                <Suspense
                  fallback={
                    <div className="flex flex-col items-center justify-center gap-3 text-muted font-mono text-xs">
                      <Activity className="w-6 h-6 text-rose animate-spin" />
                      <span>INITIALIZING 3D CHASSIS ENGINE...</span>
                    </div>
                  }
                >
                  <RealAutomationChassis3D className="h-full w-full" />
                </Suspense>
              ) : (
                <div className="flex flex-col items-center justify-center gap-3 text-muted font-mono text-xs">
                  <Activity className="w-6 h-6 text-rose animate-spin" />
                  <span>STANDBY · SCROLL TO ACTIVATE 3D CHASSIS</span>
                </div>
              )}
            </div>

            {/* Architecture Highlights Below 3D Chassis */}
            <div className="mt-6 pt-6 border-t border-white/[0.08] grid grid-cols-1 sm:grid-cols-3 gap-4 text-xs font-mono">
              <div className="p-3.5 rounded-xl bg-charcoal/90 border border-white/[0.06]">
                <span className="text-rose font-bold block mb-1">01 · BATCH DISPATCH</span>
                <span className="text-muted">Randomized human-jitter keystrokes and micro-delays mimicking natural operator pacing.</span>
              </div>
              <div className="p-3.5 rounded-xl bg-charcoal/90 border border-white/[0.06]">
                <span className="text-jade font-bold block mb-1">02 · CHECKPOINT SHIELD</span>
                <span className="text-muted">Automated phone number SMS checkpoint clearing during account bootstrap.</span>
              </div>
              <div className="p-3.5 rounded-xl bg-charcoal/90 border border-white/[0.06]">
                <span className="text-apricot font-bold block mb-1">03 · LOCAL PRIVACY CORE</span>
                <span className="text-muted">100% local PC execution. Your session data and credentials remain entirely under your control.</span>
              </div>
            </div>

          </div>

        </div>
      </section>

      {/* ── 5. The 8 Verified Capabilities (Feature Cards with Restrained Hover) ── */}
      <section id="features" className="py-16 border-t border-white/[0.08] relative z-10 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-14">
            <span className="text-xs font-mono font-bold text-rose uppercase tracking-widest block mb-2">
              Verified Capabilities Catalog
            </span>
            <h2 className="text-3xl sm:text-4xl font-black text-ivory tracking-tight">
              Built for Commercial Marketplace Scaling
            </h2>
            <p className="text-xs sm:text-sm text-muted mt-2">
              Every feature below is implemented and active in version 1.5 of the Windows desktop client.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {featureCards.map((feat) => {
              const Icon = feat.icon;
              return (
                <div
                  key={feat.title}
                  className="editorial-card p-6 rounded-2xl bg-charcoal border border-white/[0.08] hover:border-rose/30 hover:-translate-y-1 transition-all duration-300 flex flex-col justify-between group cursor-pointer"
                >
                  <div className="space-y-3.5">
                    <div className="flex items-center justify-between">
                      <div className="p-2.5 rounded-xl bg-plum-black text-rose border border-white/[0.08] group-hover:border-rose/30 group-hover:scale-105 transition-all">
                        <Icon className="w-5 h-5" />
                      </div>
                      <span className="text-[10px] font-mono text-muted uppercase tracking-wider px-2 py-0.5 rounded bg-charcoal border border-white/[0.08] group-hover:text-soft-white transition-colors">
                        {feat.tag}
                      </span>
                    </div>

                    <h3 className="text-base font-bold text-ivory group-hover:text-rose transition-colors hover-lift-text">
                      {feat.title}
                    </h3>

                    <p className="text-xs text-muted leading-relaxed">
                      {feat.desc}
                    </p>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="text-center mt-10">
            <button
              onClick={() => navigate('/features')}
              className="inline-flex items-center gap-1.5 text-xs font-mono font-bold text-rose hover:text-rose-light transition-colors cursor-pointer"
            >
              <span>Explore Complete Features Matrix & Architecture →</span>
            </button>
          </div>
        </div>
      </section>

      {/* ── 6. Strongest Download Software CTA Section ───────────────── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="p-8 sm:p-12 rounded-3xl bg-gradient-to-b from-charcoal via-plum-black to-wine border border-rose/25 shadow-elevated relative overflow-hidden">
            
            {/* Top Tag & Runtime Indicator */}
            <div className="flex flex-wrap items-center justify-between gap-3 pb-6 border-b border-white/[0.08]">
              <div className="flex items-center gap-3">
                <button
                  onClick={() => setIsDownloadModalOpen(true)}
                  className="text-xl sm:text-2xl font-mono font-black text-rose bg-rose/10 hover:bg-rose/20 px-3.5 py-1 rounded-xl border border-rose/25 transition-all cursor-pointer"
                >
                  v{CURRENT_VERSION}
                </button>
                <div>
                  <div className="text-sm font-bold text-ivory flex items-center gap-2">
                    <span>COMPILED PRODUCTION RUNTIME</span>
                    <span className="w-2 h-2 rounded-full bg-jade animate-pulse" />
                  </div>
                  <div className="text-xs text-muted font-mono">Windows 10 / 11 (64-Bit Strictly Required)</div>
                </div>
              </div>

              <span className="text-xs font-mono text-jade px-3 py-1 rounded-full bg-jade/10 border border-jade/25 font-bold">
                ✓ Verified Clean Binary ({RELEASE_CONFIG.fileSize})
              </span>
            </div>

            {/* Headline */}
            <div className="my-8 text-center sm:text-left space-y-2">
              <h2 className="text-2xl sm:text-4xl font-black text-ivory tracking-tight">
                Ready to Upgrade Your Marketplace Workflow?
              </h2>
              <p className="text-xs sm:text-sm text-muted max-w-xl">
                Download the official 64-bit setup installer directly to your PC. Runs 100% locally with hardware-backed Windows encryption.
              </p>
            </div>

            {/* Download Buttons (Rose → Vermilion) */}
            <div className="flex flex-col sm:flex-row items-center gap-4">
              {/* Direct 1-Click Native Download Button */}
              <a
                href={GOOGLE_DRIVE_DOWNLOAD_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-download-action w-full sm:w-auto px-8 py-4 rounded-xl text-white font-bold text-base flex items-center justify-center gap-2.5 cursor-pointer font-sans group"
              >
                <Download className="w-5 h-5 stroke-[2.5] group-hover:translate-y-0.5 transition-transform" />
                <span>DOWNLOAD v{CURRENT_VERSION} · ({DOWNLOAD_SIZE})</span>
              </a>

              {/* Open System Requirements & Runtime Pop-up Modal */}
              <button
                onClick={() => setIsDownloadModalOpen(true)}
                className="w-full sm:w-auto px-6 py-4 rounded-xl bg-charcoal hover:bg-plum-black text-soft-white font-bold text-sm border border-white/[0.12] flex items-center justify-center gap-2 transition-all cursor-pointer font-sans hover:-translate-y-0.5"
              >
                <Cpu className="w-4 h-4 text-rose" />
                <span>System Specs & Requirements</span>
              </button>

              <button
                onClick={() => navigate('/changelog')}
                className="w-full sm:w-auto px-5 py-4 rounded-xl bg-charcoal hover:bg-plum-black text-soft-white hover:text-white font-bold text-sm border border-white/[0.12] flex items-center justify-center gap-2 transition-all cursor-pointer font-sans hover:-translate-y-0.5"
              >
                <GitBranch className="w-4 h-4 text-rose" />
                <span>Changelog</span>
              </button>
            </div>

            {/* Release Verification & Authenticity Badge */}
            <div className="mt-8 pt-6 border-t border-white/[0.08] flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-muted">
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-4 h-4 text-jade shrink-0" />
                <span>Publisher: <strong className="text-soft-white">Lashary Softwares</strong> · 64-Bit Desktop Release</span>
              </div>
              <button
                onClick={() => navigate('/download')}
                className="flex items-center gap-1 text-rose hover:text-rose-light transition-colors self-start sm:self-auto cursor-pointer font-medium"
              >
                <span>Security & Verification Details →</span>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* ── 7. Latest Version & Changelog Highlights ───────────────── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
            <div>
              <span className="text-xs font-mono font-bold text-rose uppercase tracking-widest block mb-1">
                Release History & Highlights
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-ivory">
                What's New in Version {CURRENT_VERSION}
              </h2>
            </div>
            <button
              onClick={() => navigate('/changelog')}
              className="text-xs font-mono font-bold text-rose hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>Full Release Timeline →</span>
            </button>
          </div>

          <div className="p-8 rounded-2xl bg-charcoal border border-white/[0.08] space-y-6">
            <div>
              <h3 className="text-xs font-mono uppercase tracking-wider text-rose font-bold mb-3 flex items-center gap-2">
                <Sparkles className="w-3.5 h-3.5" />
                <span>New Features in v{CURRENT_VERSION}</span>
              </h3>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-2.5 text-xs text-soft-white">
                {currentRelease.features.slice(0, 6).map((f, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="w-3.5 h-3.5 text-jade shrink-0 mt-0.5" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="pt-4 border-t border-white/[0.08]">
              <h3 className="text-xs font-mono uppercase tracking-wider text-apricot font-bold mb-3 flex items-center gap-2">
                <ShieldCheck className="w-3.5 h-3.5" />
                <span>Key Improvements & Reliability</span>
              </h3>
              <ul className="space-y-1.5 text-xs text-muted">
                {currentRelease.improvements.slice(0, 3).map((imp, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <span className="text-apricot font-bold">→</span>
                    <span>{imp}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── 8. Blog / Resources Section ────────────────────────────── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-10">
            <div>
              <span className="text-xs font-mono font-bold text-rose uppercase tracking-widest block mb-1">
                Operator Intel & Playbooks
              </span>
              <h2 className="text-2xl sm:text-3xl font-black text-ivory">
                Marketplace Guides & Strategies
              </h2>
            </div>
            <button
              onClick={() => navigate('/blog')}
              className="text-xs font-mono font-bold text-rose hover:underline flex items-center gap-1 cursor-pointer"
            >
              <span>View All 7 Guides →</span>
            </button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {BLOG_POSTS.slice(0, 3).map((post) => (
              <article
                key={post.slug}
                onClick={() => navigate(`/blog/${post.slug}`)}
                className="editorial-card p-6 rounded-2xl bg-charcoal border border-white/[0.08] hover:border-rose/30 hover:-translate-y-1 transition-all duration-300 cursor-pointer flex flex-col justify-between group"
              >
                <div>
                  <div className="flex items-center justify-between text-[11px] font-mono text-muted mb-3">
                    <span className="text-rose font-bold uppercase">{post.category}</span>
                    <span>{post.readTime}</span>
                  </div>
                  <h3 className="text-base font-bold text-ivory group-hover:text-rose transition-colors line-clamp-2 mb-2 hover-lift-text">
                    {post.title}
                  </h3>
                  <p className="text-xs text-muted line-clamp-3 leading-relaxed mb-4">
                    {post.excerpt}
                  </p>
                </div>

                <div className="pt-3 border-t border-white/[0.08] flex items-center justify-between text-xs font-mono">
                  <span className="text-muted">{post.author}</span>
                  <span className="text-rose font-bold flex items-center gap-1 group-hover:translate-x-1 transition-transform">
                    <span>Read Guide</span>
                    <ArrowRight className="w-3 h-3" />
                  </span>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      {/* ── 9. Trust, Support & Hardware Requirements ──────────────── */}
      <section className="py-16 border-t border-white/[0.08] relative z-10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            
            {/* Direct Founder Assistance (Aswad WhatsApp) */}
            <div className="editorial-card p-8 rounded-2xl bg-charcoal border border-white/[0.08] space-y-4 flex flex-col justify-between hover:border-jade/40 hover:-translate-y-1 transition-all">
              <div>
                <h3 className="text-xl font-bold text-ivory mb-2">
                  Direct Founder Onboarding
                </h3>
                <p className="text-xs text-muted leading-relaxed">
                  No outsourced ticket queues. Core developers Abdul Mueed Lashary and Aswad Ammar Lashary provide direct WhatsApp assistance and 1-on-1 AnyDesk setup for active workstation operators.
                </p>
              </div>

              <div className="pt-2 flex flex-col sm:flex-row gap-3">
                <a
                  href={contact.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download-action flex-1 py-3 px-4 rounded-2xl text-xs font-bold flex items-center justify-center gap-2 shadow-lg shadow-rose/25 text-white hover:brightness-110 hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4 shrink-0" />
                  <span>Chat with Mueed Lashari</span>
                </a>
                <a
                  href={contact.secondaryWhatsappUrl || contact.whatsappUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-download-action flex-1 py-3 px-4 rounded-2xl text-xs font-bold flex items-center justify-center gap-2 shadow-lg shadow-rose/25 text-white hover:brightness-110 hover:-translate-y-0.5 transition-all cursor-pointer"
                >
                  <MessageCircle className="w-4 h-4 shrink-0" />
                  <span>Chat with Aswad Lashari</span>
                </a>
              </div>
            </div>

            {/* Hardware Specifications */}
            <div className="editorial-card p-8 rounded-2xl bg-charcoal border border-white/[0.08] space-y-4 hover:border-rose/30 hover:-translate-y-1 transition-all">
              <h3 className="text-xl font-bold text-ivory mb-2">
                Workstation Specifications
              </h3>
              <ul className="space-y-2.5 text-xs text-soft-white font-mono">
                <li className="flex items-center gap-2">
                  <Laptop className="w-4 h-4 text-rose shrink-0" />
                  <span>OS: Windows 10 or 11 (Strictly 64-Bit)</span>
                </li>
                <li className="flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-apricot shrink-0" />
                  <span>Browser: Google Chrome v115+ or Microsoft Edge</span>
                </li>
                <li className="flex items-center gap-2">
                  <Cpu className="w-4 h-4 text-gold shrink-0" />
                  <span>Memory: 4 GB RAM minimum (8 GB recommended)</span>
                </li>
                <li className="flex items-center gap-2">
                  <HardDrive className="w-4 h-4 text-jade shrink-0" />
                  <span>Storage: 500 MB free SSD disk space</span>
                </li>
              </ul>
            </div>

          </div>
        </div>
      </section>

      {/* ── 10. Lower-Page Ad Slot ─────────────────────────────────── */}
      <div className="max-w-5xl mx-auto px-4 sm:px-6 pt-4 relative z-10">
        <AdSlot slotType="footer" />
      </div>

    </div>
  );
};

export default HomePage;
